import { useTimer } from "@mkbabb/glass-ui/reactive";
import { customRef, getCurrentScope, onScopeDispose, ref } from "vue";
import type { Ref } from "vue";

export interface GeoCoordinates {
    lat: number;
    lng: number;
}

export type PermissionStateValue = "granted" | "prompt" | "denied" | "unsupported";

export interface GeolocationOptions {
    /** 'high' for exact lat/lng (home), 'low' for zip-code level (school). Default: 'low'. */
    accuracy?: "high" | "low";
    /**
     * How long a successful coordinate fix is considered fresh, in ms.
     * After this window expires, `requestLocation()` re-fetches instead of
     * returning the cached value. Default: 10 minutes.
     */
    freshUntilMs?: number;
}

const SAFETY_TIMEOUT_MS = 20_000;
const REQUEST_TIMEOUT_MS = 15_000;
const DEFAULT_FRESH_UNTIL_MS = 10 * 60 * 1000;

/**
 * Geolocation composable.
 *
 * Permissions-aware: tracks `permissionState` from `navigator.permissions.query`
 * with a `change` listener so revocation propagates without a refresh.
 *
 * Coord-TTL: a successful fix is cached for `freshUntilMs` (default 10 min).
 * Within that window, `requestLocation()` returns the cached coords without
 * re-prompting. Past the window, the next call re-fetches.
 *
 * `denied` short-circuits: `requestLocation()` resolves to null without
 * invoking the browser API (which would otherwise produce the same error).
 */
export function useGeolocation(opts?: GeolocationOptions) {
    const highAccuracy = opts?.accuracy === "high";
    const freshUntilMs = opts?.freshUntilMs ?? DEFAULT_FRESH_UNTIL_MS;

    const coordinates: Ref<GeoCoordinates | null> = ref(null);
    const error: Ref<string | null> = ref(null);
    const isRequesting: Ref<boolean> = ref(false);
    const coordinatesFreshAt: Ref<Date | null> = ref(null);
    const permissionState: Ref<PermissionStateValue> = ref("prompt");

    // customRef so direct reads always sample the wall clock. A plain
    // `computed` caches until a tracked dep changes, but freshness is a
    // function of `Date.now()` which is not reactive. With `customRef`,
    // every `.value` read recomputes; reactive consumers still re-run when
    // `coordinates` or `coordinatesFreshAt` mutate (their `.value` accesses
    // inside the getter establish standard ref-tracking dependencies).
    const coordinatesAreFresh: Ref<boolean> = customRef<boolean>((track) => ({
        get() {
            track();
            if (coordinates.value === null) return false;
            if (coordinatesFreshAt.value === null) return false;
            return Date.now() - coordinatesFreshAt.value.getTime() < freshUntilMs;
        },
        set() {
            // read-only by contract
        },
    }));

    let pendingRequest: Promise<GeoCoordinates | null> | null = null;
    let finishOnSafetyTimeout: (() => void) | null = null;

    const safetyTimer = useTimer(() => {
        finishOnSafetyTimeout?.();
    }, SAFETY_TIMEOUT_MS, { immediate: false });

    // Permissions API wiring. `change` listener is removed on scope dispose.
    // `Navigator.permissions` is declared by lib.dom.d.ts; the optional-chain
    // covers environments (older browsers, non-secure contexts) where the
    // property is absent at runtime despite the standard typing.
    let permissionStatus: PermissionStatus | null = null;
    const onPermissionChange = () => {
        if (!permissionStatus) return;
        permissionState.value = permissionStatus.state;
    };

    if (typeof navigator.permissions?.query !== "function") {
        permissionState.value = "unsupported";
    } else {
        navigator.permissions
            .query({ name: "geolocation" })
            .then((status) => {
                permissionStatus = status;
                permissionState.value = status.state;
                status.addEventListener("change", onPermissionChange);
            })
            .catch(() => {
                // Some environments throw on unsupported descriptors; treat as unsupported.
                permissionState.value = "unsupported";
            });
    }

    if (getCurrentScope()) {
        onScopeDispose(() => {
            if (permissionStatus) {
                permissionStatus.removeEventListener("change", onPermissionChange);
            }
        });
    }

    /**
     * Request the user's location.
     *
     * - If `permissionState === "denied"`, resolves to null without prompting.
     * - If cached coords are still fresh (within `freshUntilMs`), returns them.
     * - Otherwise calls `getCurrentPosition`. The browser handles prompting:
     *   `granted` resolves silently; `prompt` shows the UI; `denied` errors.
     * - Concurrent calls are deduplicated via the pending promise.
     */
    async function requestLocation(): Promise<GeoCoordinates | null> {
        if (pendingRequest) return pendingRequest;

        if (permissionState.value === "denied") {
            error.value = "Geolocation permission denied";
            return null;
        }

        if (coordinates.value !== null && coordinatesAreFresh.value) {
            return coordinates.value;
        }

        if (!navigator.geolocation) {
            error.value = "Geolocation not supported";
            return null;
        }

        isRequesting.value = true;
        error.value = null;

        const request = new Promise<GeoCoordinates | null>((resolve) => {
            let settled = false;

            const finish = (
                nextCoordinates: GeoCoordinates | null,
                nextError: string | null = null,
            ) => {
                if (settled) return;

                settled = true;
                safetyTimer.stop();
                finishOnSafetyTimeout = null;

                if (nextCoordinates) {
                    coordinates.value = nextCoordinates;
                    coordinatesFreshAt.value = new Date();
                }
                if (nextError !== null) {
                    error.value = nextError;
                }

                isRequesting.value = false;
                resolve(nextCoordinates);
            };

            finishOnSafetyTimeout = () => {
                finish(null, "Geolocation request timed out");
            };
            safetyTimer.start(SAFETY_TIMEOUT_MS);

            navigator.geolocation.getCurrentPosition(
                (pos) => {
                    finish({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                    });
                },
                (err) => {
                    finish(null, err.message);
                },
                { enableHighAccuracy: highAccuracy, timeout: REQUEST_TIMEOUT_MS },
            );
        }).finally(() => {
            // Clear once this request settles, regardless of when the executor's
            // resolve fired. Using `.finally` instead of clearing inside `finish`
            // avoids the synchronous-resolve race where the assignment to
            // `pendingRequest` would land *after* `finish()` had cleared it.
            pendingRequest = null;
        });

        pendingRequest = request;
        return request;
    }

    return {
        coordinates,
        error,
        isRequesting,
        permissionState,
        coordinatesFreshAt,
        coordinatesAreFresh,
        requestLocation,
    };
}
